import {
  ChangeEvent,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from "react";

import { UseNumberFieldParams } from "./NumberField.types";

const getDefaultState = <V>(value?: V, defaultValue?: V): V => {
  if (
    value !== undefined &&
    value !== null &&
    (typeof value === "number" || typeof value === "string")
  ) {
    return value;
  }
  if (
    defaultValue !== undefined &&
    defaultValue !== null &&
    (typeof defaultValue === "number" || typeof defaultValue === "string")
  ) {
    return defaultValue;
  }
  return "" as unknown as V;
};

export const useNumberField = <R>({
  ref,
  defaultValue,
  value,
  type,
  onChange,
}: UseNumberFieldParams<R>) => {
  const numberfieldRef = useRef<HTMLDivElement | null>(null);
  const [state, setState] = useState<string>(
    getDefaultState(value, defaultValue) as string
  );
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useImperativeHandle(
    ref,
    (() => {
      return {
        instance: numberfieldRef.current,
        value: state,
        setValue: (v: string) => {
          setState(v);
        },
        reset: () => {
          setState("");
        },
      };
    }) as unknown as () => R,
    [state]
  );
  const onChangeHandler = (
    event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const newValue = event.target.value;
    if (typeof value === "undefined") {
      setState(newValue);
    }
    onChange?.(newValue);
  };
  const onIncreaseHandler = () => {
    const controlled = typeof value === "undefined";
    const newValue = (Number(controlled ? state : value) + 1).toString();
    if (controlled) {
      setState(newValue);
    }
    onChange?.(newValue);
  };
  const onDecreaseHandler = () => {
    const controlled = typeof value === "undefined";
    const currentValue = Number(controlled ? state : value);
    const newValue = (currentValue === 0 ? 0 : currentValue - 1).toString();
    if (controlled) {
      setState(newValue);
    }
    onChange?.(newValue);
  };
  const onClearStateHandler = () => {
    if (typeof value === "undefined") {
      setState("");
    }
    onChange?.("");
  };

  const formatterOverride = (currentValue: string) => {
    if (type === "currency") {
      return `${currentValue}`.replace(/\B(?=(\d{3})+(?!\d))/g, ",");
    }
    if (type === "percent" && currentValue) {
      return `${`${currentValue}`.replace(/\B(?=(\d{3})+(?!\d))/g, ",")}%`;
    }
    if (type === "card" && currentValue) {
      return currentValue.replace(
        /(\d{4})(\d{4})(\d{4})(\d{4})/,
        "$1 $2 $3 $4"
      );
    }
    if (type === "phone" && currentValue) {
      return currentValue.replace(/(\d{3})(\d{3})(\d{3})/, "($1) $2 $3");
    }
    if (type === "integer" && currentValue) {
      return currentValue.replace(/[^0-9]/g, "");
    }
    return undefined;
  };

  const parserOverride = (currentValue: string) => {
    if (type === "currency") {
      return currentValue.replace(/\$\s?|(,*)/g, "");
    }
    if (type === "percent") {
      return currentValue.replace(/\$\s?|(,*)/g, "").replace("%", "");
    }
    if (type === "card") {
      return currentValue.replace(/\s/g, "");
    }
    if (type === "phone") {
      return currentValue.replace(/\D/g, "");
    }
    if (type === "integer") {
      return currentValue.replace(/[^0-9]/g, "");
    }
    return undefined;
  };

  const valueState = useMemo(
    () => (typeof value !== "undefined" ? value : state) as string,
    [value, state]
  );

  return {
    numberfieldRef,
    valueState,
    formatter: formatterOverride,
    parser: parserOverride,
    onIncreaseHandler,
    onDecreaseHandler,
    onChangeHandler,
    onClearStateHandler,
  };
};
